import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Course, CreateCourseData, Lesson, CreateLessonData, LessonUpdate, LessonFile } from '../../types';
import { coursesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface SortableFileItemProps {
  file: LessonFile;
  onDelete: (fileId: number, lessonId: number) => void;
  onDownload: (fileId: number, filename: string) => void;
}

function SortableFileItem({ file, onDelete, onDownload }: SortableFileItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'mp3':
      case 'wav':
        return '🎵';
      case 'mp4':
      case 'webm':
      case 'ogg':
        return '🎬';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
      case 'webp':
        return '🖼️';
      case 'md':
      case 'markdown':
        return '📝';
      default:
        return '📁';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
    >
      <div className="flex items-center space-x-3 flex-1">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
          title="Drag to reorder"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <span className="text-lg">{getFileIcon(file.file_type)}</span>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {file.original_filename}
          </p>
          <p className="text-xs text-gray-500">
            {file.file_type.toUpperCase()} • {formatFileSize(file.file_size)}
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => onDownload(file.id, file.original_filename)}
          className="text-blue-600 hover:text-blue-800 text-sm px-2 py-1 rounded hover:bg-blue-50 transition-colors"
          title="Download file"
        >
          ⬇️ Download
        </button>
        <button
          onClick={() => onDelete(file.id, file.lesson_id)}
          className="text-red-600 hover:text-red-800 text-sm px-2 py-1 rounded hover:bg-red-50 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

const InstructorCourseManagement: React.FC = () => {
  useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [activeTab, setActiveTab] = useState<'courses' | 'lessons'>('courses');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Course form states
  const [showCreateCourseForm, setShowCreateCourseForm] = useState(false);
  const [showEditCourseForm, setShowEditCourseForm] = useState(false);
  const [courseFormData, setCourseFormData] = useState<CreateCourseData>({
    title: '',
    description: '',
    price: 0,
  });

  // Lesson form states
  const [showCreateLessonForm, setShowCreateLessonForm] = useState(false);
  const [showEditLessonForm, setShowEditLessonForm] = useState(false);
  const [lessonFormData, setLessonFormData] = useState<CreateLessonData>({
    title: '',
    description: '',
    order: 1,
    course_id: 0,
  });
  const [uploadLoading, setUploadLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchMyCourses();
  }, []);

  const fetchMyCourses = async () => {
    try {
      setLoading(true);
      const coursesData = await coursesAPI.getMyCourses();
      setCourses(coursesData);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const newCourse = await coursesAPI.createCourse(courseFormData);
      setCourses([...courses, newCourse]);
      setShowCreateCourseForm(false);
      setCourseFormData({ title: '', description: '', price: 0 });
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse) return;

    setLoading(true);
    setError('');

    try {
      const updatedCourse = await coursesAPI.updateCourse(selectedCourse.id, courseFormData);
      setCourses(courses.map(c => c.id === selectedCourse.id ? updatedCourse : c));
      setSelectedCourse(updatedCourse);
      setShowEditCourseForm(false);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to update course');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId: number) => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    try {
      await coursesAPI.deleteCourse(courseId);
      setCourses(courses.filter(c => c.id !== courseId));
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(null);
        setActiveTab('courses');
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to delete course');
    }
  };

  const handleCreateLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse) return;

    setLoading(true);
    setError('');

    try {
      const newLesson = await coursesAPI.createLesson(selectedCourse.id, lessonFormData);
      const updatedCourse = {
        ...selectedCourse,
        lessons: [...selectedCourse.lessons, newLesson].sort((a, b) => a.order - b.order),
      };
      setCourses(courses.map(c => c.id === selectedCourse.id ? updatedCourse : c));
      setSelectedCourse(updatedCourse);
      setShowCreateLessonForm(false);
      setLessonFormData({ title: '', description: '', order: selectedCourse.lessons.length + 1, course_id: selectedCourse.id });
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLesson) return;

    setLoading(true);
    setError('');

    try {
      const updateData: LessonUpdate = {
        title: lessonFormData.title,
        description: lessonFormData.description,
        order: lessonFormData.order,
      };
      const updatedLesson = await coursesAPI.updateLesson(selectedLesson.id, updateData);
      
      if (selectedCourse) {
        const updatedCourse = {
          ...selectedCourse,
          lessons: selectedCourse.lessons.map(l => l.id === selectedLesson.id ? updatedLesson : l).sort((a, b) => a.order - b.order),
        };
        setCourses(courses.map(c => c.id === selectedCourse.id ? updatedCourse : c));
        setSelectedCourse(updatedCourse);
      }
      
      setSelectedLesson(updatedLesson);
      setShowEditLessonForm(false);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to update lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (lessonId: number) => {
    if (!window.confirm('Are you sure you want to delete this lesson? This action cannot be undone.')) {
      return;
    }

    try {
      await coursesAPI.deleteLesson(lessonId);
      
      if (selectedCourse) {
        const updatedCourse = {
          ...selectedCourse,
          lessons: selectedCourse.lessons.filter(l => l.id !== lessonId),
        };
        setCourses(courses.map(c => c.id === selectedCourse.id ? updatedCourse : c));
        setSelectedCourse(updatedCourse);
      }
      
      if (selectedLesson?.id === lessonId) {
        setSelectedLesson(null);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to delete lesson');
    }
  };

  const openEditCourseForm = (course: Course) => {
    setSelectedCourse(course);
    setCourseFormData({
      title: course.title,
      description: course.description,
      price: course.price,
    });
    setShowEditCourseForm(true);
  };

  const openEditLessonForm = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setLessonFormData({
      title: lesson.title,
      description: lesson.description || '',
      order: lesson.order,
      course_id: lesson.course_id,
    });
    setShowEditLessonForm(true);
  };

  const selectCourseAndSwitchToLessons = (course: Course) => {
    setSelectedCourse(course);
    setActiveTab('lessons');
  };

  // File management functions
  const handleFileUpload = async (lessonId: number, file: File) => {
    setUploadLoading(true);
    setError('');

    try {
      const uploadResponse = await coursesAPI.uploadFile(lessonId, file);
      
      // Update the course with the new file
      if (selectedCourse) {
        const updatedCourse = {
          ...selectedCourse,
          lessons: selectedCourse.lessons.map(lesson =>
            lesson.id === lessonId
              ? { ...lesson, files: [...(lesson.files || []), uploadResponse] }
              : lesson
          ),
        };
        setCourses(courses.map(c => c.id === selectedCourse.id ? updatedCourse : c));
        setSelectedCourse(updatedCourse);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteFile = async (fileId: number, lessonId: number) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await coursesAPI.deleteFile(fileId);
      
      // Update the course by removing the deleted file
      if (selectedCourse) {
        const updatedCourse = {
          ...selectedCourse,
          lessons: selectedCourse.lessons.map(lesson =>
            lesson.id === lessonId
              ? { ...lesson, files: (lesson.files || []).filter(file => file.id !== fileId) }
              : lesson
          ),
        };
        setCourses(courses.map(c => c.id === selectedCourse.id ? updatedCourse : c));
        setSelectedCourse(updatedCourse);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  const handleDownloadFile = async (fileId: number, filename: string) => {
    try {
      const { signed_url } = await coursesAPI.getFileSignedUrl(fileId);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = signed_url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to download file');
    }
  };

  const handleDragEnd = async (event: DragEndEvent, lessonId: number) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    if (!selectedCourse) return;

    const lesson = selectedCourse.lessons.find(l => l.id === lessonId);
    if (!lesson || !lesson.files) return;

    const oldIndex = lesson.files.findIndex(file => file.id === active.id);
    const newIndex = lesson.files.findIndex(file => file.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newFiles = arrayMove(lesson.files, oldIndex, newIndex);
    
    // Update the local state immediately for better UX
    const updatedCourse = {
      ...selectedCourse,
      lessons: selectedCourse.lessons.map(l =>
        l.id === lessonId
          ? { ...l, files: newFiles.map((file, index) => ({ ...file, order: index })) }
          : l
      ),
    };
    setCourses(courses.map(c => c.id === selectedCourse.id ? updatedCourse : c));
    setSelectedCourse(updatedCourse);

    // Send the update to the backend
    try {
      const fileOrderUpdates = newFiles.map((file, index) => ({
        file_id: file.id,
        order: index
      }));
      
      await coursesAPI.updateFileOrder(lessonId, fileOrderUpdates);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to update file order');
      // Revert the change if the API call fails
      setCourses(courses.map(c => c.id === selectedCourse.id ? selectedCourse : c));
      setSelectedCourse(selectedCourse);
    }
  };

  if (loading && courses.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('courses')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'courses'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          My Courses ({courses.length})
        </button>
        <button
          onClick={() => setActiveTab('lessons')}
          disabled={!selectedCourse}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'lessons' && selectedCourse
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-400 cursor-not-allowed'
          }`}
        >
          Manage Lessons {selectedCourse ? `(${selectedCourse.lessons.length})` : ''}
        </button>
      </div>

      {/* Courses Tab */}
      {activeTab === 'courses' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">My Courses</h2>
            <button
              onClick={() => setShowCreateCourseForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Create New Course
            </button>
          </div>

          {/* Publishing Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Publishing Note</h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Courses you create will be saved as drafts. Contact an administrator to publish your courses to make them visible to students.
                </p>
              </div>
            </div>
          </div>

          {/* Course List */}
          {courses.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-24 w-24 text-gray-400">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No courses yet</h3>
              <p className="mt-2 text-gray-500">Start by creating your first course.</p>
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateCourseForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
                >
                  Create Your First Course
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {courses.map((course) => (
                  <li key={course.id}>
                    <div className="px-4 py-4 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <p className="text-lg font-medium text-gray-900 truncate">
                              {course.title}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              ${course.price} • {course.lessons.length} lessons
                            </p>
                            <p className="text-sm text-gray-600 mt-1" style={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {course.description}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                course.is_published
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {course.is_published ? 'Published' : 'Draft'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => selectCourseAndSwitchToLessons(course)}
                          className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded text-sm font-medium"
                        >
                          Manage Lessons ({course.lessons.length})
                        </button>
                        <button
                          onClick={() => openEditCourseForm(course)}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCourse(course.id)}
                          className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Lessons Tab */}
      {activeTab === 'lessons' && selectedCourse && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Lessons for "{selectedCourse.title}"
              </h2>
              <p className="text-gray-600">Manage lessons and content for your course</p>
            </div>
            <button
              onClick={() => {
                setLessonFormData({
                  title: '',
                  description: '',
                  order: selectedCourse.lessons.length + 1,
                  course_id: selectedCourse.id,
                });
                setShowCreateLessonForm(true);
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Add Lesson
            </button>
          </div>

          {/* Lesson List */}
          {selectedCourse.lessons.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-24 w-24 text-gray-400">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No lessons yet</h3>
              <p className="mt-2 text-gray-500">Start by adding your first lesson to this course.</p>
              <div className="mt-6">
                <button
                  onClick={() => {
                    setLessonFormData({
                      title: '',
                      description: '',
                      order: 1,
                      course_id: selectedCourse.id,
                    });
                    setShowCreateLessonForm(true);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium"
                >
                  Add Your First Lesson
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {selectedCourse.lessons.map((lesson) => (
                  <li key={lesson.id}>
                    <div className="px-4 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center justify-center h-8 w-8 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                                {lesson.order}
                              </span>
                            </div>
                            <div className="flex-1">
                              <p className="text-lg font-medium text-gray-900 truncate">
                                {lesson.title}
                              </p>
                              {lesson.description && (
                                <p className="text-sm text-gray-600 mt-1" style={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}>
                                  {lesson.description}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {lesson.files?.length || 0} files attached
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditLessonForm(lesson)}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteLesson(lesson.id)}
                            className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* File Management Section */}
                      <div className="mt-4 ml-11">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-700">Files</h4>
                          <label className="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded text-sm font-medium cursor-pointer">
                            Upload File
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleFileUpload(lesson.id, file);
                                }
                              }}
                            />
                          </label>
                        </div>
                        
                        {uploadLoading && (
                          <div className="text-sm text-blue-600 mb-2">Uploading...</div>
                        )}

                        {!lesson.files || lesson.files.length === 0 ? (
                          <p className="text-sm text-gray-500">No files uploaded yet</p>
                        ) : (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleDragEnd(event, lesson.id)}
                          >
                            <SortableContext
                              items={lesson.files.sort((a, b) => (a.order || 0) - (b.order || 0)).map(f => f.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="space-y-2">
                                {lesson.files
                                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                                  .map((file) => (
                                    <SortableFileItem
                                      key={file.id}
                                      file={file}
                                      onDelete={handleDeleteFile}
                                      onDownload={handleDownloadFile}
                                    />
                                  ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Create Course Modal */}
      {showCreateCourseForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Course</h3>
              <form onSubmit={handleCreateCourse} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    required
                    value={courseFormData.title}
                    onChange={(e) => setCourseFormData({ ...courseFormData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    required
                    value={courseFormData.description}
                    onChange={(e) => setCourseFormData({ ...courseFormData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={courseFormData.price}
                    onChange={(e) => setCourseFormData({ ...courseFormData, price: parseFloat(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    📝 Course will be created as a draft. Contact an administrator to publish it.
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateCourseForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Course'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {showEditCourseForm && selectedCourse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Course</h3>
              <form onSubmit={handleUpdateCourse} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={courseFormData.title}
                    onChange={(e) => setCourseFormData({ ...courseFormData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={courseFormData.description}
                    onChange={(e) => setCourseFormData({ ...courseFormData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={courseFormData.price}
                    onChange={(e) => setCourseFormData({ ...courseFormData, price: parseFloat(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                  <p className="text-sm text-gray-600">
                    Publication Status: <span className={`font-medium ${selectedCourse.is_published ? 'text-green-600' : 'text-yellow-600'}`}>
                      {selectedCourse.is_published ? 'Published' : 'Draft'}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Only administrators can change publication status.
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditCourseForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Course'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Lesson Modal */}
      {showCreateLessonForm && selectedCourse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Lesson to "{selectedCourse.title}"
              </h3>
              <form onSubmit={handleCreateLesson} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    required
                    value={lessonFormData.title}
                    onChange={(e) => setLessonFormData({ ...lessonFormData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
                  <textarea
                    value={lessonFormData.description}
                    onChange={(e) => setLessonFormData({ ...lessonFormData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Order</label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={lessonFormData.order}
                    onChange={(e) => setLessonFormData({ ...lessonFormData, order: parseInt(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateLessonForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Adding...' : 'Add Lesson'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lesson Modal */}
      {showEditLessonForm && selectedLesson && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Lesson</h3>
              <form onSubmit={handleUpdateLesson} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={lessonFormData.title}
                    onChange={(e) => setLessonFormData({ ...lessonFormData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={lessonFormData.description}
                    onChange={(e) => setLessonFormData({ ...lessonFormData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Order</label>
                  <input
                    type="number"
                    min="1"
                    value={lessonFormData.order}
                    onChange={(e) => setLessonFormData({ ...lessonFormData, order: parseInt(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditLessonForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Lesson'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstructorCourseManagement; 